import * as cheerio from 'cheerio';
import dayjs from 'dayjs';
import { partition, some } from 'lodash-es';

export async function getSimilarAccounts(
	{
		financeClient,
		savingsAccountController,
		priorityBankingAccountController,
		fixedDepositAccountController,
		brokerageAccountController,
		cashManagementAccountController
	}: App.Locals,
	tags: BaseTag[],
	themes: BaseTheme[] = []
): Promise<SimilarAccountsData | undefined> {
	if (some(tags, { name: 'Savings Account' })) {
		const { data: savingsAccounts } = await savingsAccountController.getSavingsAccounts({
			populate: { logo: true },
			sort: ['interestRate:desc']
		});
		const [[currentAccount], otherAccounts] = partition(savingsAccounts, (savingsAccount) =>
			some(tags, { name: savingsAccount.name })
		);

		return {
			type: 'savings-account',
			accounts: [currentAccount, ...otherAccounts]
				.filter(Boolean)
				.map((savingsAccount) => ({
					isCurrent: some(tags, { name: savingsAccount.name }),
					...savingsAccount
				}))
				.slice(0, 4)
		};
	} else if (some(tags, { name: 'Priority Banking Account' })) {
		const { data: priorityBankingAccounts } =
			await priorityBankingAccountController.getPriorityBankingAccounts({
				populate: { logo: true },
				sort: ['interestRate:desc']
			});
		const [[currentAccount], otherAccounts] = partition(
			priorityBankingAccounts,
			(priorityBankingAccount) => some(tags, { name: priorityBankingAccount.name })
		);
		return {
			type: 'priority-banking-account',
			accounts: [currentAccount, ...otherAccounts]
				.filter(Boolean)
				.map((priorityBankingAccount) => ({
					isCurrent: some(tags, { name: priorityBankingAccount.name }),
					...priorityBankingAccount
				}))
				.slice(0, 4)
		};
	} else if (some(tags, { name: 'Fixed Deposit Account' })) {
		const { data: fixedDepositAccounts } =
			await fixedDepositAccountController.getFixedDepositAccounts({
				populate: { logo: true },
				sort: ['interestRate:desc']
			});
		const [[currentAccount], otherAccounts] = partition(
			fixedDepositAccounts,
			(fixedDepositAccount) => some(tags, { name: fixedDepositAccount.name })
		);
		return {
			type: 'fixed-deposit',
			accounts: [currentAccount, ...otherAccounts]
				.filter(Boolean)
				.map((fixedDepositAccount) => ({
					isCurrent: some(tags, { name: fixedDepositAccount.name }),
					...fixedDepositAccount
				}))
				.slice(0, 4)
		};
	} else if (some(tags, { name: 'Cash Management' })) {
		const fundReturn = await financeClient.get('fund-returns').json<FundReturnResponse>();
		const { data: cashManagementAccounts } =
			await cashManagementAccountController.getCashManagementAccounts({
				populate: { logo: true },
				sort: ['name:desc']
			});
		const [[currentAccount], otherAccounts] = partition(
			cashManagementAccounts,
			(cashManagementAccount) => some(tags, { name: cashManagementAccount.name })
		);
		return {
			type: 'cash-management',
			accounts: [currentAccount, ...otherAccounts]
				.filter(Boolean)
				.map((cashManagementAccount) => ({
					isCurrent: some(tags, { name: cashManagementAccount.name }),
					interestRate: fundReturn.return * 100,
					...cashManagementAccount
				}))
				.slice(0, 4)
		};
	} else if (
		some(themes, ({ uid }) => ['reits', 'stocks', 'etfs'].includes(uid)) ||
		some(tags, { name: 'Brokerage Account' })
	) {
		const { data: brokerageAccounts } = await brokerageAccountController.getBrokerageAccounts({
			populate: { logo: true },
			sort: ['name:desc']
		});
		const [[currentAccount], otherAccounts] = partition(brokerageAccounts, (brokerageAccount) =>
			some(tags, { name: brokerageAccount.name })
		);
		return {
			type: 'brokerage-account',
			accounts: [currentAccount, ...otherAccounts]
				.filter(Boolean)
				.map((brokerageAccount) => ({
					isCurrent: some(tags, { name: brokerageAccount.name }),
					...brokerageAccount
				}))
				.slice(0, 4)
		};
	}
}

export function setH2Ids(content: string) {
	const $ = cheerio.load(content);

	$('h2').each((i, el) => {
		const id = $(el).text().toLowerCase().replace(/\s/g, '-');
		$(el).attr('id', id);
	});

	return $.html();
}

export function calculateReadingTime(text: string) {
	const words = text.trim().split(/\s+/).length;
	const minutes = Math.ceil(words / 450);
	return minutes;
}

export function getArticleDates(createdAt: string, publishedAt: string) {
	const datePublished =
		dayjs(publishedAt).diff(dayjs(createdAt), 'days') < 30 ? publishedAt : createdAt;
	return {
		datePublished,
		dateModified: publishedAt
	};
}

export function isRedirectedArticle(slug: string) {
	return [
		'singapore-best-fixed-deposit-rate',
		'singapore-best-foreign-currency-fixed-deposit-rate',
		'best-savings-accounts-singapore',
		'sgs-bonds-t-bills-how-to-apply',
		'singapore-savings-bonds-ssb-complete-guide',
		'moomoo-singapore-review',
		'hsbc-everyday-global-account-ega-review',
		'cosco-shipping-singapore-chairman-gu-jing-song',
		't-bill-allotment-17-oct-2024',
		't-bill-auction-24-oct-2024',
		'why-we-like-the-latest-singapore-savings-bonds'
	].includes(slug);
}

export function rewriteArticlePath(article: Populated<BaseArticle, 'category' | 'themes'>) {
	return `/${article.category.uid}/${article.themes.length ? `${article.themes[0].uid}` : ''}/${article.slug}`;
}
